import { ANALYTICS_CONFIG } from './config';

class FieldTracker {
  constructor(analyticsService) {
    this.analytics = analyticsService;
    this.focusedField = null;
    this.focusStartTime = null;
    this.fieldValues = new Map(); // Pour détecter les changements
    this.errorsOnFields = new Map(); // Pour tracker les corrections
  }

  // Appelé quand un champ reçoit le focus
  onFieldFocus(stepNumber, fieldId, currentValue = null) {
    this.focusedField = fieldId;
    this.focusStartTime = Date.now();

    // Stocker la valeur initiale pour détecter les changements
    this.fieldValues.set(fieldId, currentValue);

    this.analytics.trackFieldFocus(stepNumber, fieldId);
  }

  // Appelé quand un champ perd le focus
  onFieldBlur(stepNumber, fieldId, currentValue = null) {
    if (this.focusedField !== fieldId) return;

    const focusDuration = Date.now() - (this.focusStartTime || Date.now());
    const previousValue = this.fieldValues.get(fieldId);
    const valueChanged = previousValue !== currentValue;
    const hasValue = currentValue !== null && currentValue !== '' && currentValue !== undefined;

    // Détecter les hésitations (focus long sans saisie)
    if (focusDuration >= ANALYTICS_CONFIG.hesitationThresholdMs && !hasValue) {
      this.analytics.trackFieldBlurEmpty(stepNumber, fieldId, focusDuration);
    }

    // Détecter les corrections après erreur
    if (valueChanged && this.errorsOnFields.has(fieldId)) {
      const errorInfo = this.errorsOnFields.get(fieldId);
      const timeSinceError = Date.now() - errorInfo.timestamp;
      this.analytics.trackFieldCorrected(stepNumber, fieldId, timeSinceError, errorInfo.errorCode);
      this.errorsOnFields.delete(fieldId);
    }

    // Tracker la complétion si valeur saisie
    if (hasValue && valueChanged) {
      this.analytics.trackFieldCompleted(stepNumber, fieldId, focusDuration);
    }

    // Notifier le session manager
    this.analytics.sessionManager.recordFieldBlur(fieldId, hasValue);

    // Reset
    this.focusedField = null;
    this.focusStartTime = null;
  }

  // Appelé quand la valeur d'un champ change
  onFieldChange(stepNumber, fieldId, newValue) {
    const previousValue = this.fieldValues.get(fieldId);

    if (previousValue !== newValue) {
      this.fieldValues.set(fieldId, newValue);
      this.analytics.sessionManager.recordFieldModified(fieldId);
    }
  }

  // Appelé quand une erreur est détectée sur un champ
  onFieldError(stepNumber, fieldId, errorCode) {
    this.errorsOnFields.set(fieldId, {
      errorCode,
      timestamp: Date.now()
    });

    this.analytics.trackValidationError(stepNumber, errorCode, fieldId);
  }

  // Helper pour créer les handlers d'événements React
  createFieldHandlers(stepNumber, fieldId, getValue) {
    return {
      onFocus: () => {
        const value = typeof getValue === 'function' ? getValue() : getValue;
        this.onFieldFocus(stepNumber, fieldId, value);
      },
      onBlur: () => {
        const value = typeof getValue === 'function' ? getValue() : getValue;
        this.onFieldBlur(stepNumber, fieldId, value);
      },
      onChange: (e) => {
        const value = e?.target?.value ?? e;
        this.onFieldChange(stepNumber, fieldId, value);
      }
    };
  }

  // Reset à chaque changement d'étape
  reset() {
    this.focusedField = null;
    this.focusStartTime = null;
    this.fieldValues.clear();
    // Note: on ne reset pas errorsOnFields pour tracker les corrections cross-step
  }

  // Obtenir l'état actuel (utile pour l'abandon)
  getCurrentState() {
    return {
      focusedField: this.focusedField,
      focusDuration: this.focusStartTime ? Date.now() - this.focusStartTime : 0,
      hasValue: this.fieldValues.get(this.focusedField) !== null
    };
  }
}

export default FieldTracker;
